// src/services/auth-service.js
// This service allows us to gradually migrate from localStorage to Firebase
// It uses Firebase when available, falls back to localStorage if not

// NOTE (Firebase Console one-time setup required):
// Authentication → Sign-in method → Enable "Google" provider → set project support email.
// This must be done before signInWithGoogle() will work.

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, isFirebaseInitialized } from '../config/firebase-config';

// Configuration - Set this to true when ready to use Firebase
const USE_FIREBASE = true; // CHANGED TO TRUE - Firebase Auth is now active

// Admin email (auto-assigned admin role + elite tier)
const ADMIN_EMAIL = 'pac623@icloud.com';

class AuthService {
  constructor() {
    this.useFirebase = USE_FIREBASE && isFirebaseInitialized();
    console.log(`🔐 AuthService initialized: Using ${this.useFirebase ? 'Firebase' : 'localStorage'}`);

    // Set up session persistence for Firebase
    if (this.useFirebase && auth) {
      setPersistence(auth, browserLocalPersistence)
        .then(() => console.log('✅ Firebase session persistence enabled'))
        .catch(err => console.error('❌ Failed to set persistence:', err));
    }
  }

  // Sign Up
  async signUp(email, password, name) {
    try {
      if (this.useFirebase && auth && db) {
        // Firebase implementation
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Update display name
        await updateProfile(user, { displayName: name });

        // Check if admin
        const isAdmin = email.toLowerCase() === ADMIN_EMAIL.toLowerCase();

        // Create user document in Firestore (role is 'pending' until role selection)
        // Practitioner document is NOT created here — it's created at /welcome if they choose 'practitioner'
        const userData = {
          uid: user.uid,
          email: email,
          name: name,
          displayName: name,
          role: isAdmin ? 'admin' : 'pending',
          subscriptionTier: isAdmin ? 'elite' : 'free',
          isAdmin: isAdmin,
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
        };

        await setDoc(doc(db, 'users', user.uid), userData);
        console.log('✅ User document created in Firestore (role: pending)');

        // Admin gets practitioner doc immediately (skip role selection)
        if (isAdmin) {
          await setDoc(doc(db, 'practitioners', user.uid), {
            profile: { displayName: name, email: email },
            services: [],
            createdAt: serverTimestamp(),
          });
        }

        // Store in localStorage for compatibility
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userEmail', email);
        localStorage.setItem('userName', name);
        localStorage.setItem('userRole', userData.role);
        localStorage.setItem('userTier', userData.subscriptionTier);
        localStorage.setItem('userId', user.uid);
        if (isAdmin) {
          localStorage.setItem('isAdmin', 'true');
        }

        return {
          success: true,
          isNewUser: true,
          user: {
            uid: user.uid,
            email: email,
            name: name,
            displayName: name,
            role: userData.role,
            subscriptionTier: userData.subscriptionTier,
            isAdmin: isAdmin
          }
        };
      } else {
        // localStorage fallback (current implementation)
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));

        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userEmail', email);
        localStorage.setItem('userName', name);
        localStorage.setItem('userRole', 'practitioner');
        localStorage.setItem('userTier', 'free');

        return {
          success: true,
          user: {
            email,
            name,
            displayName: name,
            role: 'practitioner',
            subscriptionTier: 'free'
          }
        };
      }
    } catch (error) {
      console.error('SignUp error:', error);

      // Provide user-friendly error messages
      let errorMessage = 'Failed to create account';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Please sign in instead.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Sign In
  async signIn(email, password) {
    try {
      if (this.useFirebase && auth && db) {
        // Firebase implementation
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Get user data from Firestore
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          // User exists in Auth but not in Firestore - create the document
          console.warn('User document missing in Firestore - creating now');
          const isAdmin = email.toLowerCase() === ADMIN_EMAIL.toLowerCase();

          const userData = {
            uid: user.uid,
            email: user.email,
            name: user.displayName || email.split('@')[0],
            displayName: user.displayName || email.split('@')[0],
            role: isAdmin ? 'admin' : 'practitioner',
            subscriptionTier: isAdmin ? 'elite' : 'free',
            isAdmin: isAdmin,
            createdAt: serverTimestamp(),
            lastLogin: serverTimestamp(),
          };

          await setDoc(userDocRef, userData);
        } else {
          // Update lastLogin timestamp
          await setDoc(userDocRef, {
            lastLogin: serverTimestamp()
          }, { merge: true });
        }

        // Re-fetch to get complete data
        const updatedUserDoc = await getDoc(userDocRef);
        const userData = updatedUserDoc.data();

        // Store in localStorage for compatibility
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userEmail', user.email);
        localStorage.setItem('userName', userData.name || user.displayName || email.split('@')[0]);
        localStorage.setItem('userRole', userData.role || 'practitioner');
        localStorage.setItem('userTier', userData.subscriptionTier || 'free');
        localStorage.setItem('userId', user.uid);
        if (userData.isAdmin) {
          localStorage.setItem('isAdmin', 'true');
        }

        console.log('✅ User signed in:', user.email);

        return {
          success: true,
          user: {
            uid: user.uid,
            email: user.email,
            name: userData.name,
            displayName: userData.displayName,
            role: userData.role,
            subscriptionTier: userData.subscriptionTier,
            isAdmin: userData.isAdmin || false
          }
        };
      } else {
        // localStorage fallback (current implementation)
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));

        // In dev mode, accept any credentials
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userEmail', email);
        localStorage.setItem('userName', email.split('@')[0]);
        localStorage.setItem('userRole', 'practitioner');
        localStorage.setItem('userTier', 'free');

        return {
          success: true,
          user: {
            email,
            name: email.split('@')[0],
            displayName: email.split('@')[0],
            role: 'practitioner',
            subscriptionTier: 'free'
          }
        };
      }
    } catch (error) {
      console.error('SignIn error:', error);

      // Provide user-friendly error messages
      let errorMessage = 'Invalid email or password';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email. Please sign up.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Sign In with Google (OAuth popup)
  // Returns { success, isNewUser, user } or { success: false, error }
  // error is null for silent failures (popup closed/cancelled by user)
  async signInWithGoogle() {
    if (!this.useFirebase || !auth || !db) {
      return { success: false, error: 'Google sign-in requires Firebase to be enabled.' };
    }

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const additionalInfo = getAdditionalUserInfo(result);
      const isNewUser = additionalInfo?.isNewUser ?? false;
      const isAdmin = user.email.toLowerCase() === ADMIN_EMAIL.toLowerCase();
      const displayName = user.displayName || user.email.split('@')[0];

      if (isNewUser) {
        // Create user document only (role: pending until role selection)
        // Practitioner document created at /welcome if they choose 'practitioner'
        const userData = {
          uid: user.uid,
          email: user.email,
          name: displayName,
          displayName: displayName,
          role: isAdmin ? 'admin' : 'pending',
          subscriptionTier: isAdmin ? 'elite' : 'free',
          isAdmin: isAdmin,
          authProvider: 'google',
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
        };
        await setDoc(doc(db, 'users', user.uid), userData);

        // Admin gets practitioner doc immediately
        if (isAdmin) {
          await setDoc(doc(db, 'practitioners', user.uid), {
            profile: { displayName, email: user.email, photo: user.photoURL || null },
            services: [],
            createdAt: serverTimestamp(),
          });
        }
        console.log('✅ New Google user — user doc created (role: pending)');

        // Set localStorage for backward compatibility
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userEmail', user.email);
        localStorage.setItem('userName', displayName);
        localStorage.setItem('userRole', userData.role);
        localStorage.setItem('userTier', userData.subscriptionTier);
        localStorage.setItem('userId', user.uid);
        if (isAdmin) localStorage.setItem('isAdmin', 'true');

        return {
          success: true,
          isNewUser: true,
          user: { uid: user.uid, email: user.email, name: displayName, role: userData.role },
        };
      } else {
        // Existing user — load Firestore data
        const userDocRef = doc(db, 'users', user.uid);
        let userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          // Google auth exists but no Firestore doc (edge case) — create it
          const userData = {
            uid: user.uid,
            email: user.email,
            name: displayName,
            displayName: displayName,
            role: isAdmin ? 'admin' : 'practitioner',
            subscriptionTier: isAdmin ? 'elite' : 'free',
            isAdmin: isAdmin,
            authProvider: 'google',
            createdAt: serverTimestamp(),
            lastLogin: serverTimestamp(),
          };
          await setDoc(userDocRef, userData);
          userDoc = await getDoc(userDocRef);
        } else {
          await setDoc(userDocRef, { lastLogin: serverTimestamp() }, { merge: true });
          userDoc = await getDoc(userDocRef);
        }

        const userData = userDoc.data();

        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userEmail', user.email);
        localStorage.setItem('userName', userData.name || displayName);
        localStorage.setItem('userRole', userData.role || 'practitioner');
        localStorage.setItem('userTier', userData.subscriptionTier || 'free');
        localStorage.setItem('userId', user.uid);
        if (userData.isAdmin) localStorage.setItem('isAdmin', 'true');

        console.log('✅ Existing Google user signed in:', user.email);

        return {
          success: true,
          isNewUser: false,
          user: { uid: user.uid, email: user.email, name: userData.name, role: userData.role },
        };
      }
    } catch (error) {
      console.error('Google sign-in error:', error);

      // Silent failures — user dismissed the popup, no error shown
      if (
        error.code === 'auth/popup-closed-by-user' ||
        error.code === 'auth/cancelled-popup-request'
      ) {
        return { success: false, error: null };
      }

      let errorMessage = 'Google sign-in failed. Please try again.';
      if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked. Please allow popups for this site and try again.';
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'An account with this email already exists. Try signing in with Google instead.';
      }

      return { success: false, error: errorMessage };
    }
  }

  // Sign Out
  async signOut() {
    try {
      if (this.useFirebase && auth) {
        await firebaseSignOut(auth);
        console.log('✅ Firebase sign out successful');
      }

      // Always clear localStorage
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userName');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userTier');
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('userId');
      localStorage.removeItem('sessionId');

      // Clear practitioner-specific data
      localStorage.removeItem('firstTime_practitioner');
      localStorage.removeItem('practitionerOnboardingComplete');

      return { success: true };
    } catch (error) {
      console.error('SignOut error:', error);
      return { success: false, error: error.message };
    }
  }

  // Password Reset
  async resetPassword(email) {
    try {
      if (this.useFirebase && auth) {
        await sendPasswordResetEmail(auth, email);
        console.log('✅ Password reset email sent to:', email);
        return { success: true };
      } else {
        // localStorage mode - just simulate
        await new Promise(resolve => setTimeout(resolve, 500));
        console.log('📧 [Mock] Password reset email sent to:', email);
        return { success: true };
      }
    } catch (error) {
      console.error('Password reset error:', error);

      let errorMessage = 'Failed to send reset email';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    if (this.useFirebase && auth) {
      return auth.currentUser !== null;
    }
    return localStorage.getItem('isAuthenticated') === 'true';
  }

  // Get current user (synchronous - may be incomplete during Firebase init)
  getCurrentUser() {
    if (this.useFirebase && auth && auth.currentUser) {
      return {
        uid: auth.currentUser.uid,
        email: auth.currentUser.email,
        name: localStorage.getItem('userName') || auth.currentUser.displayName,
        displayName: auth.currentUser.displayName,
        role: localStorage.getItem('userRole') || 'practitioner',
        subscriptionTier: localStorage.getItem('userTier') || 'free',
        isAdmin: localStorage.getItem('isAdmin') === 'true'
      };
    }

    // localStorage fallback
    const isAuth = localStorage.getItem('isAuthenticated') === 'true';
    if (isAuth) {
      return {
        email: localStorage.getItem('userEmail'),
        name: localStorage.getItem('userName'),
        displayName: localStorage.getItem('userName'),
        role: localStorage.getItem('userRole') || 'practitioner',
        subscriptionTier: localStorage.getItem('userTier') || 'free',
        isAdmin: localStorage.getItem('isAdmin') === 'true'
      };
    }

    return null;
  }

  // Get current user from Firestore (async - complete data)
  async getCurrentUserFromFirestore() {
    if (this.useFirebase && auth && db && auth.currentUser) {
      try {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // Update localStorage cache
          localStorage.setItem('userName', userData.name);
          localStorage.setItem('userRole', userData.role);
          localStorage.setItem('userTier', userData.subscriptionTier);
          if (userData.isAdmin) {
            localStorage.setItem('isAdmin', 'true');
          }
          return userData;
        }
      } catch (error) {
        console.error('Error fetching user from Firestore:', error);
      }
    }
    return this.getCurrentUser();
  }

  // Set user role
  async setUserRole(role) {
    try {
      localStorage.setItem('userRole', role);

      if (this.useFirebase && auth && db && auth.currentUser) {
        // Update role in Firestore
        await setDoc(doc(db, 'users', auth.currentUser.uid), {
          role: role,
          roleUpdatedAt: serverTimestamp()
        }, { merge: true });
      }

      return { success: true };
    } catch (error) {
      console.error('SetUserRole error:', error);
      return { success: false, error: error.message };
    }
  }

  // Listen for auth state changes (Firebase only)
  onAuthStateChange(callback) {
    if (this.useFirebase && auth) {
      return onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          // User is signed in - fetch Firestore data
          try {
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              callback({
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                name: userData.name,
                displayName: userData.displayName,
                role: userData.role,
                subscriptionTier: userData.subscriptionTier,
                isAdmin: userData.isAdmin || false
              });
            } else {
              // Fallback to Firebase Auth data
              callback({
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
                displayName: firebaseUser.displayName,
                role: 'practitioner',
                subscriptionTier: 'free',
                isAdmin: false
              });
            }
          } catch (error) {
            console.error('Error in onAuthStateChange:', error);
            callback(null);
          }
        } else {
          // User is signed out
          callback(null);
        }
      });
    }

    // For localStorage, just call the callback with current state
    const user = this.getCurrentUser();
    callback(user);
    return () => {}; // Return empty unsubscribe function
  }

  // Toggle between Firebase and localStorage (for testing)
  toggleAuthMode() {
    this.useFirebase = !this.useFirebase && isFirebaseInitialized();
    console.log(`🔄 Switched to ${this.useFirebase ? 'Firebase' : 'localStorage'} mode`);
    return this.useFirebase;
  }

  // Get current auth mode
  getAuthMode() {
    return this.useFirebase ? 'firebase' : 'localStorage';
  }
}

let authService;
try {
  authService = new AuthService();
  console.log('✅ AuthService instance created successfully');
} catch (error) {
  console.error('❌ Failed to create AuthService instance:', error);
  // Create a fallback object with basic methods
  authService = {
    getAuthMode: () => 'error',
    getCurrentUser: () => null,
    signIn: async () => ({ success: false, error: 'Service not initialized' }),
    signUp: async () => ({ success: false, error: 'Service not initialized' }),
    signOut: async () => ({ success: false, error: 'Service not initialized' }),
    resetPassword: async () => ({ success: false, error: 'Service not initialized' }),
    toggleAuthMode: () => false,
    isAuthenticated: () => false,
    onAuthStateChange: () => () => {}
  };
}

export default authService;
